import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { listUsers } from "@/services/userService";
import { type User } from "../../amplify/api/types";
import { UserForm } from "@/components/UserForm";
import { Badge } from "@/components/ui/badge";
import { Edit, UserPlus } from "lucide-react";

export function UsersPage() {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const users = await listUsers();
            setUsers(users);
        } catch (error) {
            console.error("Error fetching users:", error);
            toast.error("Failed to load users");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSuccess = () => {
        setIsCreateDialogOpen(false);
        fetchUsers();
    };

    const handleEditUser = (username: string) => {
        navigate(`/users/${username}`);
    };

    return (
        <div className="container mx-auto ">
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>User Management</CardTitle>
                    <Button
                        onClick={() => setIsCreateDialogOpen(true)}
                        className="flex items-center gap-2"
                    >
                        <UserPlus className="h-4 w-4" />
                        Create User
                    </Button>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="text-center py-4">Loading users...</div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Username</TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Groups</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {users.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={6}
                                            className="text-center"
                                        >
                                            No users found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    users.map((user) => (
                                        <TableRow key={user.username}>
                                            <TableCell>
                                                {user.username}
                                            </TableCell>
                                            <TableCell>
                                                {user.givenName ||
                                                user.familyName ? (
                                                    `${user.givenName || ""} ${user.familyName || ""}`.trim()
                                                ) : (
                                                    <span className="text-gray-400">
                                                        Not set
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant={
                                                        user.enabled
                                                            ? "default"
                                                            : "destructive"
                                                    }
                                                >
                                                    {user.userStatus}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex flex-wrap gap-1">
                                                    {user.groups ? (
                                                        user.groups.map(
                                                            (group) => (
                                                                <Badge
                                                                    key={group}
                                                                    variant="outline"
                                                                >
                                                                    {group}
                                                                </Badge>
                                                            )
                                                        )
                                                    ) : (
                                                        <span className="text-gray-400">
                                                            None
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() =>
                                                        handleEditUser(
                                                            user.username
                                                        )
                                                    }
                                                    className="flex items-center gap-1"
                                                >
                                                    <Edit className="h-3 w-3" />
                                                    Edit
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <Dialog
                open={isCreateDialogOpen}
                onOpenChange={setIsCreateDialogOpen}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create New User</DialogTitle>
                    </DialogHeader>
                    <UserForm onSuccess={handleCreateSuccess} />
                </DialogContent>
            </Dialog>
        </div>
    );
}
